import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { closeLease, createLease, listLeases, runLeaseMonth } from "../api/acctIfrs";
import { AcctShell } from "../features/acct/AcctShell";
import { LeaseForm } from "../features/acct/LeaseForm";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";
import { useApiError } from "../hooks/useApiError";

export default function AcctLeasesPage() {
  const { t } = useTranslation();
  const describe = useApiError();
  const { data, reload: load } = useCachedResource("acct-leases", () => listLeases().catch(() => []));
  const rows = data || [];
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const runMonth = async () => {
    try {
      const res = await runLeaseMonth(month);
      toast.success(res.posted ? `${t("acct.ifrs.leasePosted")} ${res.entry_number}` : t(`acct.errors.${res.reason}`));
      load();
    } catch (error) {
      toast.error(describe(error));
    }
  };

  return (
    <AcctShell titleKey="leases">
      <LeaseForm onSaved={load} onCreate={createLease} />

      <NeuCard className="flex flex-wrap items-end gap-3 p-5" data-testid="lease-month-box">
        <div className="w-44">
          <p className="label-xs mb-2">{t("acct.ifrs.leaseMonth")}</p>
          <NeuInput type="month" data-testid="lease-month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <NeuButton data-testid="lease-run" onClick={runMonth}>{t("acct.ifrs.runLeaseMonth")}</NeuButton>
        <p className="text-[11px] text-muted-foreground">{t("acct.ifrs.leaseMonthHint")}</p>
      </NeuCard>

      <div className="grid gap-2" data-testid="lease-list">
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="lease-empty">{t("acct.ifrs.noLeases")}</p>
        ) : null}
        {rows.map((row) => (
          <NeuCard key={row.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`lease-row-${row.id}`}>
            <span className="min-w-0 flex-1 truncate text-sm font-bold">{row.name}</span>
            <span className="font-mono text-xs" dir="ltr">{row.start_date}</span>
            <span className="font-mono text-xs" dir="ltr">{row.term_months} m</span>
            <span className="font-mono text-xs" dir="ltr">{row.initial_value}</span>
            <span className="font-mono text-xs text-muted-foreground" dir="ltr">{row.accumulated_amortisation}</span>
            <span className="font-mono text-sm font-bold" dir="ltr">{row.rou_book_value}</span>
            <span className="font-mono text-xs text-primary" dir="ltr">{row.liability}</span>
            <span className="text-[11px] font-bold">{t(`acct.ifrs.leaseStatus.${row.status}`)}</span>
            {row.status === "active" ? (
              <NeuButton
                className="px-3 py-2 text-xs"
                data-testid={`lease-close-${row.id}`}
                onClick={async () => {
                  await closeLease(row.id);
                  load();
                }}
              >
                {t("acct.ifrs.closeLease")}
              </NeuButton>
            ) : null}
          </NeuCard>
        ))}
      </div>
    </AcctShell>
  );
}
